import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import isEqual from "lodash/isEqual";
import pickBy from "lodash/pickBy";
import {
    Typography,
    Button,
    Divider,
    Stack,
    TextField,
    IconButton,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { addSearchFilters } from "reducers/candidate/searchFiltersDataSlice";
import removeEmptyProperties from "utils/removeEmptyProperties";
import SpecificCandidateFilters from "./SpecificCandidateFilters";
import RecruitmentProcessFilters from "./RecruitmentProcessFilters";
import PoolCreationFilters from "./PoolCreationFilters";
import KeywordSearchHelpDialog from "./KeywordSearchHelpDialog";
import { useSearchParams } from "react-router-dom";

const extractIds = (dataArray) =>
    dataArray.flatMap((item) => (item.id ? [item.id] : []));

const DEFAULT_FILTER_VALUES = {
    q: "",
    // Pool creation
    location: [],
    locationRadius: null,
    skills: [],
    jobFunctions: [],
    occupations: [],
    yearOfExperienceMinimum: "",
    yearOfExperienceMaximum: "",
    preferredIndustries: [],
    preferredJobFunctions: [],
    preferredOccupations: [],
    preferredSkills: [],
    workingModels: [],
    jobTypes: [],
    currencyId: null,
    salaryMinimum: "",
    salaryMaximum: "",
    // Specific candidate
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    linkedin_profile: "",
    collegelink_id: "",
    // Recruitment processes
    recruiters: [],
    jobs: [],
    companies: [],
    currentStateStatus: false,
    rp_states: [],
    rp_statuses: [],
    rp_status_start_date: "",
    rp_status_end_date: "",
};

const parseFiltersParam = (searchParams) => {
    try {
        return JSON.parse(searchParams.get("filters")) || {};
    } catch {
        return {};
    }
};

export const Filters = () => {
    const [isKeywordHelpOpen, setIsKeywordHelpOpen] = useState(false);
    const [resetCount, setResetCount] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    const defaultValues = {
        ...DEFAULT_FILTER_VALUES,
        ...parseFiltersParam(searchParams),
    };

    const {
        control,
        handleSubmit,
        register,
        watch,
        reset,
        setValue,
        getValues,
        formState: { errors },
    } = useForm({ defaultValues });

    const formData = {
        control,
        handleSubmit,
        register,
        watch,
        reset,
        setValue,
        getValues,
        errors,
        resetCount,
    };

    const changedValues = pickBy(
        watch(),
        (value, name) => !isEqual(value, DEFAULT_FILTER_VALUES[name]),
    );
    const filtersParam = JSON.stringify(changedValues);

    const dispatch = useDispatch();

    //Clean keys not needed in the back
    const tidyUpFormData = (formData) => {
        const cleanedData = removeEmptyProperties(formData);
        return cleanedData;
    };

    useEffect(() => {
        setSearchParams(
            filtersParam === "{}" ? {} : { filters: filtersParam },
            { replace: true },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersParam]);

    const onSubmit = (filterData) => {
        const {
            currentStateStatus,
            yearOfExperienceMinimum,
            yearOfExperienceMaximum,
            salaryMinimum,
            salaryMaximum,
        } = filterData;

        if (yearOfExperienceMinimum || yearOfExperienceMaximum) {
            filterData.years_of_experience = [
                yearOfExperienceMinimum || 0, //If no min value is set, default to 0
                yearOfExperienceMaximum || 100, //If no max value is set, default to 100
            ];
        }

        if (salaryMinimum || salaryMaximum) {
            filterData.salary = [
                salaryMinimum || 0, //If no min value is set, default to 0
                salaryMaximum || 999999, //If no max value is set, default to 999999
            ];
        }

        filterData.rp_statuses = filterData.rp_statuses.flatMap(
            (item) => item.ids,
        );

        filterData.rp_states = filterData.rp_states.flatMap((item) => item.ids);
        filterData.currentStateStatus = Number(currentStateStatus);

        filterData.preferredIndustries = extractIds(
            filterData.preferredIndustries,
        );
        filterData.preferredJobFunctions = extractIds(
            filterData.preferredJobFunctions,
        );
        filterData.preferredOccupations = extractIds(
            filterData.preferredOccupations,
        );
        filterData.preferredSkills = extractIds(filterData.preferredSkills);

        filterData.currencyId = filterData.currencyId?.id || null;

        removeEmptyProperties(filterData);

        const preparedData = tidyUpFormData(filterData);

        dispatch(addSearchFilters(preparedData));
    };

    const handleDynamicSubmitForm = (event) => {
        event.preventDefault();
        handleSubmit(onSubmit)();
        event.target.blur();
        if (document.activeElement) document.activeElement.blur();
    };

    const applyKeywordSample = (query) => {
        setValue("q", query, { shouldDirty: true });
        handleSubmit(onSubmit)();
    };

    return (
        <form onSubmit={handleDynamicSubmitForm}>
            <Stack
                direction="column"
                px={2}
                py={3}
                spacing={2}
                sx={{ position: "relative" }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                >
                    <Typography variant="header1" component="h3">
                        Filters
                    </Typography>
                    <Button
                        sx={{
                            textTransform: "none",
                            padding: 0,
                        }}
                        variant="text"
                        onClick={(event) => {
                            reset(DEFAULT_FILTER_VALUES);
                            setResetCount((count) => count + 1);
                            handleDynamicSubmitForm(event);
                        }}
                    >
                        Clear all
                    </Button>
                    <Button sx={{ display: "none" }} type="submit"></Button>
                </Stack>

                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Stack direction="row" alignItems="center">
                            <Typography
                                id="candidate-keyword-search-label"
                                variant="subtitle2"
                                component="label"
                                htmlFor="candidate-keyword-search"
                            >
                                Keyword Search
                            </Typography>
                            <IconButton
                                aria-label="Keyword search help"
                                onClick={() => setIsKeywordHelpOpen(true)}
                                size="small"
                            >
                                <HelpOutlineIcon fontSize="1rem" />
                            </IconButton>
                        </Stack>
                        <TextField
                            {...register("q")}
                            name="q"
                            id="candidate-keyword-search"
                            placeholder='e.g. react AND ("data engineer" OR developer)'
                            helperText='Use AND, OR, and "phrases". See examples for groups and wildcards.'
                            onBlur={handleDynamicSubmitForm}
                            variant="standard"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={8}
                            inputProps={{ maxLength: 150 }}
                        />
                    </Stack>

                    <KeywordSearchHelpDialog
                        open={isKeywordHelpOpen}
                        onClose={() => setIsKeywordHelpOpen(false)}
                        onApplySample={applyKeywordSample}
                    />

                    <Divider />

                    <PoolCreationFilters
                        handleSubmit={handleDynamicSubmitForm}
                        formData={formData}
                    />

                    <Divider />

                    <SpecificCandidateFilters
                        handleSubmit={handleDynamicSubmitForm}
                        formData={formData}
                    />

                    <Divider />

                    <RecruitmentProcessFilters
                        handleSubmit={handleDynamicSubmitForm}
                        formData={formData}
                    />
                </Stack>
            </Stack>
        </form>
    );
};

export default Filters;
